import { supabase } from "@/integrations/supabase/client";
import { createClient } from "@supabase/supabase-js";

function createSimulationClient(sessionId: string) {
  return createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    { global: { headers: { "x-simulation-session": sessionId } } }
  );
}
import { extractPdfTextBrowser } from "@/services/pdf-extract";
import { extractXlsxTextBrowser } from "@/services/xlsx-extract";
import { AppError, toAppError } from "@/lib/errors";
import { fetchJson } from "@/lib/http";
import { updateExtractedAnalysisByPath } from "@/services/docread.dal";

export type UploadResult = {
  storagePath: string;
  publicUrl?: string;
  documentId?: string;
  tempSessionId?: string;
};

// Upload temporário (sem paciente/consulta) - usa simulation_documents para temp
export async function uploadTemporaryFile(
  file: File,
  bucket = "patient-docs"
): Promise<UploadResult> {
  const ext = file.name.split(".").pop()!.toLowerCase();
  const tempSessionId = crypto.randomUUID();
  const docId = crypto.randomUUID();
  const path = `temp/${tempSessionId}/${docId}/${file.name}`;

  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    upsert: false,
    contentType: file.type || `application/octet-stream`,
  });
  if (error) throw new Error(`Upload error: ${error.message}`);

  const fileUrl = `${bucket}/${path}`;
  const simClient = createSimulationClient(tempSessionId);

  const { data: ed, error: edErr } = await simClient
    .from("simulation_documents")
    .insert({
      temp_session_id: tempSessionId,
      file_name: file.name,
      file_type: file.type,
      file_url: fileUrl,
      health_info_type: "outros",
      expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    })
    .select("id")
    .single();

  if (edErr) throw new Error(`DB insert simulation_documents: ${edErr.message}`);

  return { storagePath: path, documentId: ed.id, tempSessionId };
}

const MAX = 25 * 1024 * 1024;
const ALLOWED = ["pdf", "docx", "xlsx", "xls", "jpg", "jpeg", "png"];

// Upload com paciente/consulta definidos
export async function uploadPatientFile(
  patientId: string,
  appointmentId: string,
  file: File,
  bucket = "patient-docs"
): Promise<UploadResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new AppError("AUTH", "User not authenticated");
    const ext = file.name.split(".").pop()!.toLowerCase();
    const docId = crypto.randomUUID();
    const path = `${user.id}/${patientId}/${docId}/${file.name}`;

    const { error } = await supabase.storage.from(bucket).upload(path, file, {
      upsert: false,
      contentType: file.type || `application/octet-stream`,
    });
    if (error) throw new AppError("STORAGE_UPLOAD", error.message);


    const { data: ed, error: edErr } = await supabase
      .from("documents_onboarding")
      .insert({
        patient_id: patientId,
        owner_id: user.id,
        file_name: file.name,
        file_type: ext,
        file_path: path,
        file_size: file.size,
        status: 'uploaded',
      })
      .select("id")
      .single();

    if (edErr) throw new AppError("DB_UPDATE", edErr.message);

    return { storagePath: path, documentId: ed.id };
  } catch (e) {
    throw toAppError(e, "STORAGE_UPLOAD");
  }
}

export async function callExtractText(storagePath: string, fileType: string, file?: File) {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/extract-text`;
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  try {
    const data = await fetchJson(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey },
      body: JSON.stringify({ bucket: "patient-docs", path: storagePath, fileType }),
    });
    return data.text as string;
  } catch (e: any) {
    if (e?.status === 400) throw new AppError("EDGE_400", e?.payload?.error || "Erro 400", e?.payload);
    if (e?.status >= 500) throw new AppError("EDGE_500", e?.payload?.error || "Erro 5xx", e?.payload);
    throw new AppError("EDGE_NETWORK", e?.message || "Falha de rede", e);
  }
}

export async function callAiAnalyze(text: string, patient?: any) {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-analyze`;
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  try {
    return await fetchJson(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey },
      body: JSON.stringify({ text, patient }),
    });
  } catch (e: any) {
    throw new AppError("AI_FAILED", e?.payload?.error || e?.message || "Falha IA", e);
  }
}

export async function saveExtraction(appointmentId: string, fileName: string, storagePath: string, extracted: string) {
  const { error } = await supabase
    .from("documents_onboarding")
    .update({
      extracted_text: extracted,
      status: 'extracted'
    })
    .eq("file_name", fileName)
    .eq("file_path", storagePath);
  if (error) throw new Error(`DB update documents_onboarding: ${error.message}`);
}

// Salvar extração para documento temporário (por ID) - usa simulation_documents
export async function saveTemporaryExtraction(documentId: string, extracted: string, sessionId?: string) {
  const client = sessionId ? createSimulationClient(sessionId) : supabase;
  const { error } = await client
    .from("simulation_documents")
    .update({ extracted_content: extracted })
    .eq("id", documentId);
  if (error) throw new Error(`DB update simulation_documents: ${error.message}`);
}

// Atualizar análise de documento em documents_onboarding
export async function updateDocumentAnalysis(
  documentId: string,
  analysis: {
    extracted_text?: string;
    ai_summary?: string;
    ai_insights?: any;
    ai_risk_level?: string;
    lab_results?: string;
    has_lab_data?: boolean;
    status?: string;
  }
) {
  const { error } = await supabase
    .from("documents_onboarding")
    .update({
      ...analysis,
      status: analysis.status || 'analyzed'
    })
    .eq("id", documentId);

  if (error) throw new AppError("DB_UPDATE", error.message);
}

// Vincular documento temporário a um paciente/consulta - copia de simulation_documents → documents_onboarding
export async function linkDocumentToPatient(documentId: string, appointmentId: string) {
  // 1. Buscar documento de simulation_documents
  const { data: simDoc, error: fetchErr } = await supabase
    .from("simulation_documents")
    .select("*")
    .eq("id", documentId)
    .single();

  if (fetchErr) throw new AppError("DB_UPDATE", fetchErr.message);

  // 2. Buscar patient_id do appointment
  const { data: apt, error: aptErr } = await supabase
    .from("appointments")
    .select("patient_id")
    .eq("id", appointmentId)
    .maybeSingle();

  if (aptErr) throw new AppError("DB_UPDATE", aptErr.message);
  if (!apt) throw new AppError("DB_UPDATE", "Appointment not found");

  // 3. Buscar owner_id do paciente
  const { data: patient, error: patErr } = await supabase
    .from("patients_onboarding")
    .select("owner_id")
    .eq("id", apt.patient_id)
    .maybeSingle();

  if (patErr) throw new AppError("DB_UPDATE", patErr.message);
  if (!patient) throw new AppError("DB_UPDATE", "Patient not found");

  // 4. Criar em documents_onboarding
  const { error: insertErr } = await supabase
    .from("documents_onboarding")
    .insert({
      patient_id: apt.patient_id,
      owner_id: patient.owner_id,
      file_path: simDoc.file_url,
      file_name: simDoc.file_name,
      file_type: simDoc.file_type,
      file_size: 0, // não temos esse dado em simulation_documents
      status: 'analyzed',
      extracted_text: simDoc.extracted_content,
      ai_summary: simDoc.ai_summary,
      ai_insights: simDoc.ai_insights,
      ai_risk_level: simDoc.ai_risk_level,
      source: 'express_upload'
    });

  if (insertErr) throw new AppError("DB_UPDATE", insertErr.message);

  // 5. Deletar de simulation_documents
  await supabase
    .from("simulation_documents")
    .delete()
    .eq("id", documentId);
}

/**
 * Orquestrador completo: valida → upload → extração → IA → salvar
 */
export async function processFileWithAI(
  patientId: string,
  appointmentId: string,
  file: File,
  patientMeta?: any
) {
  // Validação
  if (!file || file.size === 0) throw new AppError("FILE_EMPTY", "Arquivo vazio.");
  if (file.size > MAX) throw new AppError("FILE_TOO_BIG", "Arquivo maior que 25MB.");
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!ALLOWED.includes(ext)) throw new AppError("FILE_UNSUPPORTED", `.${ext}`);

  // Upload + registro
  const { storagePath } = await uploadPatientFile(patientId, appointmentId, file);

  // Extração
  let extractedText = "";
  try {
    if (ext === "pdf") {
      extractedText = await extractPdfTextBrowser(file);
    } else if (ext === "xlsx" || ext === "xls") {
      extractedText = await extractXlsxTextBrowser(file);
    } else {
      extractedText = await callExtractText(storagePath, ext, file);
    }
  } catch (e: any) {
    if (ext === "pdf") throw new AppError("PDF_WORKER", e?.message || "Falha PDF", e);
    if (ext === "xlsx" || ext === "xls") throw new AppError("XLSX_PARSER", e?.message || "Falha XLSX", e);
    throw toAppError(e, "EDGE_500");
  }
  if (!extractedText?.trim()) throw new AppError("any", "Texto não foi extraído.");

  // IA
  const analyzed = await callAiAnalyze(extractedText, patientMeta);

  // Persistência
  const filePath = storagePath;
  try {
    await updateExtractedAnalysisByPath(patientId, file.name, filePath, {
      lab_results: analyzed.lab_results,
      summary: analyzed.analysis.summary,
      insights: analyzed.analysis.insights,
      risk_level: analyzed.analysis.risk_level,
    });
  } catch (e: any) {
    if (/violates row-level security|RLS|permission/i.test(e?.message)) {
      throw new AppError("RLS_DENY", e.message);
    }
    throw new AppError("DB_UPDATE", e.message);
  }

  return {
    storagePath,
    lab_results: analyzed.lab_results,
    analysis: analyzed.analysis,
    fileName: file.name
  };
}
